/* Minimal Server-Sent-Events frame parser.
 *
 * The browser's native `EventSource` cannot send an `Authorization`
 * header, but the admin stream is Bearer-gated (access token lives in
 * memory, bug #18) — so we consume the stream over `fetch` + a
 * `ReadableStream` reader and parse the wire format ourselves. This
 * module is the pure, synchronous core so it can be unit-tested
 * without a network. See `client.ts` for the fetch/reconnect loop. */

export interface SseEvent {
  /** `event:` line value; defaults to "message" per the SSE spec. */
  event: string;
  /** `id:` line value, if present. */
  id: string | null;
  /** Concatenated `data:` lines (newline-joined). */
  data: string;
}

/** Parse complete events out of an accumulating buffer.
 *
 * Returns the events found and the unparsed remainder (a partial
 * event still being streamed). Events are delimited by a blank line
 * (`\n\n`). Lines starting with `:` are comments (keep-alive) and are
 * ignored. */
export function parseSse(buffer: string): { events: SseEvent[]; rest: string } {
  const events: SseEvent[] = [];
  // Normalise CRLF so the split is uniform.
  const normalized = buffer.replace(/\r\n/g, '\n');
  const blocks = normalized.split('\n\n');
  // The final element is either "" (buffer ended on a delimiter) or a
  // partial block we must hold for the next chunk.
  const rest = blocks.pop() ?? '';

  for (const block of blocks) {
    if (block.trim() === '') continue;
    const ev = parseBlock(block);
    if (ev) events.push(ev);
  }
  return { events, rest };
}

function parseBlock(block: string): SseEvent | null {
  let event = 'message';
  let id: string | null = null;
  const dataLines: string[] = [];
  let sawField = false;

  for (const rawLine of block.split('\n')) {
    if (rawLine.startsWith(':')) continue; // comment / keep-alive
    const colon = rawLine.indexOf(':');
    const field = colon === -1 ? rawLine : rawLine.slice(0, colon);
    // Per spec, a single leading space after the colon is stripped.
    let value = colon === -1 ? '' : rawLine.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);

    if (field === 'event') {
      event = value;
      sawField = true;
    } else if (field === 'id') {
      id = value;
      sawField = true;
    } else if (field === 'data') {
      dataLines.push(value);
      sawField = true;
    }
  }

  if (!sawField) return null;
  return { event, id, data: dataLines.join('\n') };
}
